import type { ClientPortalSummary } from '@/lib/api/client-portal.api'
import type { QueryClient } from '@tanstack/react-query'
import { isNotificationReaderRefused } from '@/lib/query/reader-refusals'
import { portalMetadataKeys } from '@/modules/client-portal/metadata.api'

import { isValidUUID } from '@scrambl/shared'

import type { ThreadDraftOwner } from './drafts'

export interface ThreadAccountSnapshot {
  ownerUserId: string | null
  key: unknown
  signal: AbortSignal
  assertUnchanged(): void
}
export function portalThreadSummaryKey(
  scope: ThreadAccountSnapshot,
  entityId: string,
) {
  return portalMetadataKeys.summary(entityId, {
    workspaceSession: scope.key,
    portalEntityId: entityId,
  })
}
/** Identity/routing only. The application service must check current source and
 * action policy again on every read or command. No staff-context fallback. */
export function portalThreadOwner(
  scope: ThreadAccountSnapshot,
  entityId: string,
  summary: ClientPortalSummary | undefined,
): { owner: ThreadDraftOwner; preview: boolean } | null {
  try {
    scope.assertUnchanged()
  } catch {
    return null
  }
  if (
    scope.signal.aborted ||
    !isValidUUID(scope.ownerUserId ?? '') ||
    !isValidUUID(entityId) ||
    !summary ||
    summary.userId !== scope.ownerUserId ||
    summary.entity?.id !== entityId ||
    !isValidUUID(summary.organization?.id ?? '') ||
    summary.settings?.isEnabled !== true ||
    typeof summary.isInternalViewer !== 'boolean'
  )
    return null
  return {
    owner: {
      userId: scope.ownerUserId!,
      organizationId: summary.organization.id,
      portalEntityId: entityId,
      externalRelationshipId: null,
    },
    preview: summary.isInternalViewer,
  }
}
/** Reconnect uses only the exact original account/access-owned summary reader.
 * Missing/denied/preview proof retains queued work without adopting another org. */
export function readRegisteredPortalThreadOwner(
  client: QueryClient,
  scope: ThreadAccountSnapshot,
  entityId: string,
): ThreadDraftOwner | null {
  const key = portalThreadSummaryKey(scope, entityId)
  const query = client.getQueryCache().find({ queryKey: key, exact: true })
  if (
    !query ||
    query.state.status !== 'success' ||
    isNotificationReaderRefused(client, key)
  )
    return null
  const context = portalThreadOwner(
    scope,
    entityId,
    query.state.data as ClientPortalSummary,
  )
  return context && !context.preview ? context.owner : null
}
